import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Form,
  Input,
  Select,
  message,
  Card,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;
const { Option } = Select;
const VAN_TYPES = ['LWB 4.2', 'Luton', 'Large Luton', 'Curtain side', 'Large Curtain Side', '7.5T Curtain side', 'Car'];

const OpcVans = () => {
  const [vans, setVans] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingKey, setEditingKey] = useState(null);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const firstFieldRef = useRef(null);

  const fetchVans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.vans.getAll();
      setVans(Array.isArray(res.data) ? res.data : res.data?.vans || []);
    } catch {
      message.error('Failed to load vans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVans();
  }, [fetchVans]);

  const isEditing = (record) => record._id === editingKey;

  const startEdit = (record) => {
    editForm.setFieldsValue({
      registration: record.registration,
      make: record.make,
      model: record.model,
      vanType: record.vanType || undefined,
    });
    setEditingKey(record._id);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    editForm.resetFields();
  };

  const saveEdit = async (record) => {
    try {
      const values = await editForm.validateFields();
      values.registration = (values.registration || '').toUpperCase();
      await apiService.vans.update(record._id, values);
      setEditingKey(null);
      message.success('Saved');
      fetchVans();
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      values.registration = (values.registration || '').toUpperCase();
      await apiService.vans.create(values);
      addForm.resetFields();
      message.success('Van added');
      fetchVans();
      setTimeout(() => firstFieldRef.current?.focus(), 100);
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const columns = [
    {
      title: 'Registration',
      dataIndex: 'registration',
      key: 'registration',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="registration" noStyle rules={[{ required: true, message: ' ' }]}>
            <Input size="small" style={{ width: 160, textTransform: 'uppercase' }} placeholder="Registration" />
          </Form.Item>
        ) : (
          <Text strong>{v}</Text>
        ),
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="make" noStyle>
            <Input size="small" style={{ width: 150 }} placeholder="Make" />
          </Form.Item>
        ) : (
          v || <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="model" noStyle>
            <Input size="small" style={{ width: 150 }} placeholder="Model" />
          </Form.Item>
        ) : (
          v || <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Van Type',
      dataIndex: 'vanType',
      key: 'vanType',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="vanType" noStyle>
            <Select size="small" style={{ width: 140 }} placeholder="Type" allowClear>
              {VAN_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </Form.Item>
        ) : (
          v || <Text type="secondary">—</Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 72,
      align: 'right',
      render: (_, record) =>
        isEditing(record) ? (
          <Space size={2}>
            <Button type="text" size="small" icon={<CheckOutlined style={{ color: '#3f8600' }} />} onClick={() => saveEdit(record)} />
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEdit} />
          </Space>
        ) : (
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(record)} />
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>My Vans</Title>
      </div>

      {/* Add form */}
      <Card
        size="small"
        style={{ marginBottom: 12, borderTop: `2px solid ${COLOR_PRIMARY}`, background: 'rgba(22,119,255,0.06)' }}
        bodyStyle={{ padding: '10px 12px' }}
      >
        <Form form={addForm} onFinish={handleAdd}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={labelStyle}>Registration *</div>
              <Form.Item name="registration" noStyle rules={[{ required: true, message: ' ' }]}>
                <Input ref={firstFieldRef} size="small" style={{ width: 160, textTransform: 'uppercase' }} placeholder="e.g. CN75 NPY" />
              </Form.Item>
            </div>
            <div>
              <div style={labelStyle}>Make</div>
              <Form.Item name="make" noStyle>
                <Input size="small" style={{ width: 150 }} placeholder="Make" />
              </Form.Item>
            </div>
            <div>
              <div style={labelStyle}>Model</div>
              <Form.Item name="model" noStyle>
                <Input size="small" style={{ width: 150 }} placeholder="Model" />
              </Form.Item>
            </div>
            <div>
              <div style={labelStyle}>Van Type</div>
              <Form.Item name="vanType" noStyle>
                <Select size="small" style={{ width: 140 }} placeholder="Type" allowClear>
                  {VAN_TYPES.map(t => <Option key={t} value={t}>{t}</Option>)}
                </Select>
              </Form.Item>
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <Button type="primary" size="small" htmlType="submit" icon={<PlusOutlined />}>Add</Button>
            </div>
          </div>
        </Form>
      </Card>

      {/* Vans table */}
      <Form form={editForm} component={false}>
        <Table
          dataSource={vans}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="small"
          style={{ borderRadius: 8, overflow: 'hidden' }}
          locale={{ emptyText: 'No vans yet — add one above' }}
        />
      </Form>
    </div>
  );
};

const labelStyle = { fontSize: 11, color: '#888', marginBottom: 2 };

export default OpcVans;
